import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import {
  createRef,
  FocusEventHandler,
  FunctionComponent,
  useState,
} from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';

import {
  brandAtom,
  setModelAtom,
  setversionAtom,
} from '../../../../../hooks/use-picker-selection';
import { fetchBrands } from '../../../../../services/vehicles';
import Select, {
  CustomClearIndicator,
  CustomDownChevron,
  CustomOptionBrand,
  CustomSingleValueBrand,
} from '../../../../ui/inputs/custom-selector';

export const brandRef = createRef();

const loadBrands = async ({ queryKey: [_, locale] }) => {
  const data = await fetchBrands({ locale });

  return data.children.map((item) => ({
    value: item.slug,
    label: item.name,
    logo: item.logo_url,
  }));
};

const BrandSelect: FunctionComponent<{
  onFocus?: FocusEventHandler;
  register?: UseFormRegisterReturn;
  error?: Array<string>;
  onChange?: (value: any) => void;
  setValue?: any;
  menuPlacement?: 'auto' | 'top' | 'bottom';
}> = ({
  onFocus,
  register,
  error,
  onChange,
  menuPlacement = 'auto',
  setValue,
}) => {
    const { locale } = useRouter();
    const { t } = useTranslation();
    const [enabled, setEnabled] = useState(false);
    const [brand, setBrand] = useAtom(brandAtom);
    const [, setModel] = useAtom(setModelAtom);
    const [, setVersion] = useAtom(setversionAtom);

    const { data: brands, isLoading: isBrandsLoading } = useQuery(
      ['search-brands', locale],
      loadBrands,
      {
        enabled,
      }
    );

    return (
      <Select
        {...register}
        ref={brandRef}
        aria-label={t('vehicle-picker:chooseBrand')}
        id="brand-input"
        openMenuOnFocus
        value={brand}
        onFocus={(e) => {
          if (onFocus) onFocus(e);
          setEnabled(true);
        }}
        loadingMessage={() => t('common:loading')}
        options={brands}
        isLoading={enabled && isBrandsLoading}
        components={{
          Option: CustomOptionBrand,
          SingleValue: CustomSingleValueBrand,
          DropdownIndicator: CustomDownChevron,
          ClearIndicator: CustomClearIndicator,
        }}
        onChange={(item) => {
          if (onChange) onChange(item || '');
          setValue('slug', { brand: item });
          setBrand(item ? { ...item, slug: item.value } : null);
          setModel(null);
          setVersion(null);
        }}
        placeholder={t('vehicle-picker:chooseBrand')}
        error={error}
        isClearable
        menuPlacement={menuPlacement}
      />
    );
  };

export default BrandSelect;
